import { useAssociatedTenants } from './hooks/useAssociatedTenants';
import { useExternalSignOut } from './hooks/useExternalSignOut';
import { useAppTerms } from './hooks/useAppTerms';
import { useAppLanguages } from './hooks/useAppLanguages';
import { useAppMultitenant } from './hooks/useAppMultitenant';
import { useAppPageTitle } from './hooks/useAppPageTitle';
import { IonApp, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './global.less';

import React, {
  StrictMode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IConfig, IConfigApp, IEnvironment } from './interfaces/environment';
import configJson from './config.json';
import { UserService } from './services/UserService';
import { IRole } from './interfaces/role';
import { PageLoading } from '@ant-design/pro-layout';
import { App as AntdApp, ConfigProvider, theme } from 'antd';
import type { ModalFuncProps } from 'antd';
import { IUser, IUserFirebase } from './interfaces/user';
import { ContextApp } from './contexts/ContextApp';
import AppContent from './AppContent';
import { useCustomMessage } from './hooks';
import { EnumsValues, LocalStorageKeys, Themes } from './enums/EnumsValues';
import { IDBPDatabase } from 'idb';
import { useServiceWorker } from './hooks/useServiceWorker';
import esES from 'antd/es/locale/es_ES';
import enUS from 'antd/locale/en_US';
import { TParameter, TReturn } from './i18n/i18n.model';
import { TOptions } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Authorization, Tools } from './shared';
import { useSubscription } from '@apollo/client/react';
import { Subscription } from './services/graphql/subscription';

declare global {
  interface Window {
    firebaseui: {
      auth: {
        AuthUI: {
          new (auth: unknown): {
            start: (container: string, config: object) => void;
          };
          getInstance: () => {
            start: (container: string, config: object) => void;
          } | null;
        };
      };
    };
  }
}

type AppMessageError = { context: string; error: unknown };
type AppModalConfirm = {
  context: string;
  message?: string;
  props: ModalFuncProps;
};

/* No importar de forma directa, utilizar lazy, ya que estos componentes
activan y desactivan sus propias stylesheets, y si son importados normalmente
podrían desactivar reglas de otras stylesheets */
const LightTheme = React.lazy(() => import('./theme/light/LightTheme'));
const DarkTheme = React.lazy(() => import('./theme/dark/DarkTheme'));

export type T = <S extends TParameter>(p: S, options?: TOptions) => TReturn<S>;

const AppMessageBridge = ({
  t,
  onReady,
}: {
  t: T;
  onReady: (actions: {
    showMessageError: (op: AppMessageError) => void;
    messageModalConfirm: (op: AppModalConfirm) => void;
  }) => void;
}) => {
  const { showMessageError, messageModalConfirm, notificationError } =
    useCustomMessage(t);

  useEffect(() => {
    onReady({ showMessageError, messageModalConfirm });
  }, [onReady, showMessageError, messageModalConfirm]);

  useEffect(() => {
    const handleAppMessageError = (event: Event) => {
      const errorEvent = event as CustomEvent<{ message?: string }>;
      if (errorEvent.detail?.message) {
        notificationError({
          context: 'AppMessageBridge.processQueryError',
          props: { message: errorEvent.detail.message },
        });
      }
    };

    window.addEventListener('app:message-error', handleAppMessageError);
    return () => {
      window.removeEventListener('app:message-error', handleAppMessageError);
    };
  }, [notificationError]);

  return null;
};

const App: React.FC = () => {
  setupIonicReact({
    platform: {
      /** The default `desktop` function returns false for devices with a touchscreen.
       * This is not always wanted, so this function tests the User Agent instead.
       **/
      desktop: (win) => {
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            win.navigator.userAgent,
          );
        return !isMobile;
      },
    },
  });
  const [i18nT, i18n] = useTranslation();
  const [configApp] = useState<IConfigApp>(configJson);
  const [user, setUser] = useState<IUser | undefined>();
  useExternalSignOut(
    !!user,
    user?.id,
    () => {
      // Reload drops the previous interface and in-flight App state. Credentials
      // were removed by the originating tab; don't clear storage or call logout.
      window.location.replace('/login');
    },
    () => {
      // Another tab replaced the shared browser session with another account.
      // Reload before issuing requests with that account's credentials.
      window.location.replace('/app/home');
    },
  );
  const [roles, setRoles] = useState<IRole[]>([]);
  const [functions, setFunctions] = useState<string[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [environment, setEnvironment] = useState<IEnvironment>();
  const [config, setConfig] = useState<IConfig>();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const pageTitle = useAppPageTitle();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [showLoading, setShowLoading] = useState<boolean>(true);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  useEffect(() => {
    const expireSession = () => setSessionExpired(true);
    window.addEventListener('sessionExpired', expireSession);
    return () => window.removeEventListener('sessionExpired', expireSession);
  }, []);
  const [mainMenuCollapsed, setMainMenuCollapsed] = useState(
    window.innerWidth < 992,
  );
  const [menuBreakpointCollapsed, setMenuBreakpointCollapsed] = useState(
    window.innerWidth < 992,
  );
  const languages = useAppLanguages(i18n, user?.language_id);
  const [themeMode, setThemeMode] = useState<string>(Themes.DEFAULT);
  const { authenticate, authenticateData } = UserService();
  const [userDataFirebase, setUserDataFirebase] = useState<
    IUserFirebase | undefined
  >(undefined);
  const [pictureProfileUrl, setPictureProfileUrl] = useState<string>('');
  const [show2FAReminder, setShow2FAReminder] = useState<boolean>(false);
  const [selectedTenantId, setSelectedTenantId] = useState<number>();
  const [fullNameMandatory, setFullNameMandatory] = useState<boolean>();
  const isAppMultitenant = useAppMultitenant();

  const translate = i18nT as (key: string, options?: TOptions) => string;
  const t: T = (p, options) => {
    return translate(p, { ...options, fallbackLng: 'es' }) as TReturn<typeof p>;
  };

  const appMessageActionsRef = useRef<{
    showMessageError: (op: AppMessageError) => void;
    messageModalConfirm: (op: AppModalConfirm) => void;
  } | null>(null);
  const setAppMessageActions = useCallback(
    (actions: {
      showMessageError: (op: AppMessageError) => void;
      messageModalConfirm: (op: AppModalConfirm) => void;
    }) => {
      appMessageActionsRef.current = actions;
    },
    [],
  );
  const showMessageError = useCallback((op: AppMessageError) => {
    appMessageActionsRef.current?.showMessageError(op);
  }, []);
  const messageModalConfirm = useCallback((op: AppModalConfirm) => {
    appMessageActionsRef.current?.messageModalConfirm(op);
  }, []);

  const {
    termsConfigurationLoading,
    isRequiredAcceptTemrsAndConditions,
    setIsRequiredAcceptTemrsAndConditions,
    termsAndConditionsVersionActive,
    setTermsAndConditionsVersionActive,
    userHasAcceptedTermsAndConditions,
    setUserHasAcceptedTermsAndConditions,
    getTermsAndConditions,
    getUserHasAcceptedTermsAndConditions,
  } = useAppTerms(
    user?.id,
    () => Tools.getStorage(EnumsValues.LocalStorageKeys.UserId),
    showMessageError,
  );

  const {
    tenantsAssociatedToUser,
    setTenantsAssociatedToUser,
    getTenantsAssociatedWithUser,
  } = useAssociatedTenants(
    user?.id,
    () => Tools.getStorage(EnumsValues.LocalStorageKeys.UserId),
    showMessageError,
    isAppMultitenant ||
      Authorization.security(functions, EnumsValues.Functions.IsAdmin),
  );

  const defaultEnvironmentCode = configJson.default_environment;
  const defaultEnvironment = (
    configJson.environments as {
      [key: string]: IEnvironment;
    }
  )[defaultEnvironmentCode];
  const defaultConfig = configJson.default_config;
  const indexedDatabaseRef = useRef<IDBPDatabase<unknown> | null>(null);
  const { applyUpdate, postponeFinishedNotification, showWorkerNotification } =
    useServiceWorker();

  const subscriptionResponse = useSubscription(Subscription.userPlanUpdated, {
    variables: { tenant_id: selectedTenantId },
  });

  useEffect(() => {
    /*
     * Al cargar la aplicación, si no se cuenta con mutaciones pendientes,
     * se eliminan los valores optimistas a reemplazar (optimisticValuesToReplace)
     * para liberar espacio del local storage
     */

    const trackedQueries =
      Tools.getStorage(LocalStorageKeys.TrackedQueries) || [];
    if (trackedQueries.length === 0) {
      Tools.setStorage(LocalStorageKeys.optimisticValuesToReplace, []);
    }
  }, []);

  useEffect(() => {
    if (showWorkerNotification) {
      messageModalConfirm({
        context: 'App.serviceWorkerUpdate',
        props: {
          content: <div>{i18nT('abm.updateAvailable')}</div>,
          cancelText: i18nT('action.noThanks'),
          okText: i18nT('abm.updateNow'),
          maskClosable: true,
          onOk: () => applyUpdate(),
          onCancel: () => postponeFinishedNotification(),
        },
      });
    }
  }, [
    showWorkerNotification,
    applyUpdate,
    postponeFinishedNotification,
    messageModalConfirm,
  ]);

  useEffect(() => {
    if (authenticateData) {
      const {
        email,
        id,
        username,
        person,
        roles,
        is_system_user,
        language_id,
        theme,
        profile_id,
        profile,
        uid,
        two_factor_auth_active,
        signature_id,
        validated,
        accepted_terms_and_conditions,
        permissions,
        features: authenticatedFeatures,
        last_tenant_id,
      } = authenticateData;

      const authenticatedUser: IUser = {
        email,
        id,
        username,
        password: '',
        person,
        roles,
        is_system_user,
        language_id,
        theme,
        profile_id,
        profile,
        uid,
        two_factor_auth_active,
        signature_id,
        validated,
        accepted_terms_and_conditions,
        last_tenant_id,
      };
      // The hook loads a new account; reauthentication refreshes the existing one.
      if (id === user?.id) void getTenantsAssociatedWithUser();
      Tools.setStorage(
        EnumsValues.LocalStorageKeys.LastTenantId,
        String(last_tenant_id),
      );
      setSelectedTenantId(last_tenant_id);
      setUser(authenticatedUser);
      setRoles(roles);
      if (two_factor_auth_active) {
        const validate2FA = Tools.getStorage(
          EnumsValues.LocalStorageKeys.TwoFactorValidated,
        );
        if (!validate2FA) {
          Tools.setStorage(
            EnumsValues.LocalStorageKeys.TwoFactorValidated,
            false,
          );
        }
      }
      setPictureProfileUrl(person?.[0]?.profile_picture?.url ?? '');
      setFunctions(permissions ?? []);
      setFeatures(authenticatedFeatures || []);
    }
  }, [authenticateData]);

  const checkAuth = async (lastTenantId?: number): Promise<boolean> => {
    setShowLoading(true);
    try {
      const authenticatedUser = await authenticate(lastTenantId);
      await getTermsAndConditions();
      await getUserHasAcceptedTermsAndConditions(authenticatedUser.id);
      return true;
    } catch (error) {
      showMessageError({
        context: 'App.checkAuth.1',
        error,
      });
      return false;
    } finally {
      setShowLoading(false);
    }
  };

  useEffect(() => {
    if (user?.theme === Themes.DARK && themeMode !== Themes.DARK) {
      setThemeMode(Themes.DARK);
    } else if (themeMode !== Themes.LIGHT) {
      setThemeMode(Themes.LIGHT);
    }
  }, [user]);

  useEffect(() => {
    if (user?.person?.[0]?.profile_picture?.url != null) {
      setPictureProfileUrl(user.person?.[0]?.profile_picture?.url);
    }
  }, [user]);

  const getFeatures = useCallback(async () => {
    try {
      await authenticate(selectedTenantId);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (user) {
      getFeatures().catch((error) => {
        showMessageError({ context: 'App.getFeatures', error });
      });
    }
  }, [subscriptionResponse.data]);

  useEffect(() => {
    const token = Tools.getStorage(EnumsValues.LocalStorageKeys.Token);
    if (!user && token) {
      void checkAuth();
    } else {
      setShowLoading(() => false);
    }
  }, []);

  const currentLanguage = useMemo(() => {
    switch (i18n.language) {
      case 'en':
        return enUS;
      case 'es':
        return esES;
      default:
        return esES;
    }
  }, [i18n.language]);

  return (
    <ContextApp
      value={{
        configApp,
        user,
        setUser,
        setLoading,
        loading,
        loadingMessage,
        setLoadingMessage,
        setShowLoading,
        showLoading,
        environment: environment || defaultEnvironment,
        setEnvironment,
        roles,
        setRoles,
        functions,
        setFunctions,
        features,
        setFeatures,
        config: config || defaultConfig,
        setConfig,
        dataLoaded,
        setDataLoaded,
        checkAuth,
        mainMenuCollapsed,
        setMainMenuCollapsed,
        menuBreakpointCollapsed,
        setMenuBreakpointCollapsed,
        sessionExpired,
        setSessionExpired,
        languages,
        userDataFirebase,
        setUserDataFirebase,
        pictureProfileUrl,
        setPictureProfileUrl,
        show2FAReminder,
        setShow2FAReminder,
        indexedDatabaseRef,
        maintenanceMode,
        setMaintenanceMode,
        isRequiredAcceptTemrsAndConditions,
        setIsRequiredAcceptTemrsAndConditions,
        t,
        termsAndConditionsVersionActive,
        setTermsAndConditionsVersionActive,
        userHasAcceptedTermsAndConditions,
        setUserHasAcceptedTermsAndConditions,
        getTermsAndConditions,
        fullNameMandatory,
        setFullNameMandatory,
        tenantsAssociatedToUser,
        setTenantsAssociatedToUser,
        getTenantsAssociatedWithUser,
        selectedTenantId,
        setSelectedTenantId,
        isAppMultitenant,
      }}
    >
      {pageTitle && <title>{pageTitle}</title>}
      <IonApp>
        <ConfigProvider
          locale={currentLanguage}
          theme={{
            algorithm:
              themeMode === Themes.DARK ? theme.darkAlgorithm : undefined,
            token: {
              colorPrimary: '#454f58',
            },
          }}
        >
          <AntdApp className="AppShell">
            <AppMessageBridge t={t} onReady={setAppMessageActions} />
            {(themeMode === Themes.DARK && <DarkTheme />) || <LightTheme />}
            {!showLoading && (!user || !termsConfigurationLoading) ? (
              <IonReactRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <StrictMode>
                  <AppContent />
                </StrictMode>
              </IonReactRouter>
            ) : (
              <PageLoading />
            )}
          </AntdApp>
        </ConfigProvider>
      </IonApp>
    </ContextApp>
  );
};

export default App;
